import { GameConfig } from "@/config/GameConfig";
import type { RandomSource } from "@/core/rng";
import type { LevelStats, CampaignLevelResult } from "@/progress/ProgressStorage";
import { isQaModeAllowedHost } from "./hostAllowlist";

/**
 * Preview-only QA test modes for manually exercising WORMHOLE/STARFRUIT
 * without waiting for their real rarity. Deliberately framework-free
 * (no `window`/DOM access here) so the hostname/URL parsing logic is
 * plain, unit-testable functions - `resolveQaMode` is the single choke
 * point every caller (GameScene, RunTracker's callers, ...) goes through,
 * so no caller can accidentally read a QA mode without the host check
 * already having been applied.
 */
/** `"endless"` is Endless Growth V1's own single QA mode - see `GameScene`'s Endless branch. It also enables the same deterministic CHAOS-forcing overrides `"chaos"` does (minus STARFRUIT forcing, which would contradict "STARFRUIT never spawns in Endless"), so there's no separate `"endless-chaos"` value. */
/**
 * `"unlock-campaign"` is a presentation-only override for World
 * Select/Level Select (see `isSelectableForQa()` below) - manual QA on
 * any Campaign world/level without first completing every level before
 * it. It never touches `ProgressStorage`: real unlock/completion/star
 * state is untouched, so removing `?qa=unlock-campaign` shows the
 * player's real progress exactly as it was.
 */
export type QaModeKind = "starfruit" | "wormhole" | "scoring" | "speed" | "feedback" | "chaos" | "endless" | "unlock-campaign";

/**
 * Re-exported from `./hostAllowlist` (which has zero imports of its own,
 * unlike this file) so every existing caller of `QaMode.isQaModeAllowedHost`
 * keeps working unchanged - see that module's doc comment for why the
 * predicate itself lives there instead of here.
 */
export { isQaModeAllowedHost } from "./hostAllowlist";

/**
 * Parses `?qa=starfruit` / `?qa=wormhole` from a URL query string, but
 * only ever returns non-null when `hostname` passes
 * `isQaModeAllowedHost` - the host gate is inseparable from parsing, so
 * there is no code path that reads a QA mode value without it already
 * having been checked against the allowlist. Any other/missing/invalid
 * `qa` value (including on an allowed host) resolves to `null`.
 */
export function resolveQaMode(hostname: string | null | undefined, search: string): QaModeKind | null {
  if (!isQaModeAllowedHost(hostname)) return null;
  const raw = new URLSearchParams(search).get("qa");
  return raw === "starfruit" ||
    raw === "wormhole" ||
    raw === "scoring" ||
    raw === "speed" ||
    raw === "feedback" ||
    raw === "chaos" ||
    raw === "endless" ||
    raw === "unlock-campaign"
    ? raw
    : null;
}

/**
 * Whether `?qa=unlock-campaign` is active on an allowed QA host - the
 * single check World Select/Level Select use to decide whether to render
 * a world/level as selectable regardless of real progress. Goes through
 * the exact same `resolveQaMode()`/`isQaModeAllowedHost()` gate every
 * other QA mode uses, so this can never activate on a disallowed host
 * (production, or any hostname outside the allowlist).
 */
export function isQaCampaignUnlockActive(hostname: string | null | undefined, search: string): boolean {
  return resolveQaMode(hostname, search) === "unlock-campaign";
}

/**
 * Whether a Campaign world/level should render as selectable in World
 * Select/Level Select: really unlocked, OR the QA-only
 * `unlock-campaign` override is active. Deliberately takes the real
 * unlock state as a plain boolean (never `ProgressStorage` itself) and
 * returns a plain boolean (never writes anything) - a pure
 * presentation-layer combinator, extracted as its own named function
 * (the same reason `shouldPersistRun` is) so this can be unit-tested
 * despite World Select/Level Select themselves having no Scene test
 * harness in this project.
 */
export function isSelectableForQa(reallyUnlocked: boolean, qaCampaignUnlockActive: boolean): boolean {
  return qaCampaignUnlockActive || reallyUnlocked;
}

/** The label shown by the small on-screen QA indicator - kept here so GameScene never hand-formats it differently. */
export function qaModeLabel(mode: QaModeKind): string {
  return `QA MODE: ${mode.toUpperCase()}`;
}

/**
 * A `starfruitRandom` source that is always eligible (always below
 * `GameConfig.starfruit.probability`, whatever it's currently configured
 * as) - used only for `?qa=starfruit`. STARFRUIT still only actually
 * reveals in the real second normal fruit group; forcing eligibility
 * changes nothing else about the real placement/scheduling rules.
 */
export function forceStarfruitEligibleRandom(): RandomSource {
  return () => 0;
}

/**
 * A `chaosRandom` source that resolves exactly the *next* CHAOS pick to
 * WORMHOLE, then falls through to `fallback` (real `Math.random` by
 * default) for every roll after that - used only for `?qa=wormhole`.
 * Never changes the real CHAOS probability table: the forced roll is a
 * single value chosen from that same table's WORMHOLE band, not a
 * different rule.
 */
export function forceNextChaosWormholeRandom(fallback: RandomSource = Math.random): RandomSource {
  let consumed = false;
  return () => {
    if (!consumed) {
      consumed = true;
      // The WORMHOLE band is [jackpot, jackpot + wormhole) - its own
      // lower bound is always inside it, whatever the two weights are
      // currently configured as. QA mode always drives Campaign, which
      // always rolls the current (PR13-A) pool - never the legacy one.
      return GameConfig.bites.chaos.outcomeWeightsCurrentV2.jackpot;
    }
    return fallback();
  };
}

/**
 * The single rule for "is this run allowed to touch the outside world":
 * submitted to the leaderboard/Supabase, queued for retry, or saved to
 * `ProgressStorage`. `null` (no QA mode active - every real production
 * run, and an ordinary Deploy Preview visit with no `?qa=`) is the only
 * case that persists; either QA mode never does. Extracted as its own
 * named, directly-testable function (the same reason
 * `completionCoordinator.decideNamePrompt` exists) since `GameScene`
 * itself - a Phaser `Scene` - has no unit test harness in this project;
 * this is the part of that decision that can be, and is, verified in
 * isolation.
 */
export function shouldPersistRun(qaMode: QaModeKind | null): boolean {
  return qaMode === null;
}

/**
 * The QA-mode-only equivalent of `ProgressStorage.recordLevelResult()` -
 * same return shape as the fields the Level Complete overlay reads, but
 * a pure function of already-known values: it never reads or writes
 * `ProgressStorage` itself (the caller supplies `previous`, read via
 * `ProgressStorage.getLevelStats()` - reading existing progress is fine,
 * only writing isn't). `result.stars` arrives already computed by the
 * caller (see `computeCampaignCompletionResult`) from this run's
 * completed Final Score - this function never computes stars itself, it
 * only compares against whatever is *already* saved, so a QA session can
 * never claim a "NEW STAR RECORD"/"NEW HIGH SCORE" that doesn't actually
 * get kept, and never reports a world/unlock celebration nothing has
 * actually unlocked.
 */
export function computeQaOnlyOverlayOutcome(
  result: CampaignLevelResult,
  previous: LevelStats,
): {
  stars: 1 | 2 | 3;
  isNewStarRecord: boolean;
  isNewLegacyHighScore: boolean;
  newlyUnlockedWorld?: undefined;
  newlyCompletedWorld?: undefined;
} {
  return {
    stars: result.stars,
    isNewStarRecord: result.stars > previous.stars,
    isNewLegacyHighScore: result.biteScore > previous.highScore,
    newlyUnlockedWorld: undefined,
    newlyCompletedWorld: undefined,
  };
}
